import React, { useMemo } from 'react';
import type { TableColumnsType } from 'antd';
import { Button, Table } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../graphql/queries/getChannelAll';
import { OrderType } from '../../../graphql/types/OrderType';
import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';

interface DamagedLabelLookupTableProps {
    parcelData: OrderType[];
    showParcelDetail: (record: OrderType) => void;
    onSelectChange?: (selectedRowKeys: React.Key[], selectedRows: OrderType[]) => void;
    selectedRowKeys?: React.Key[];
    loading?: boolean;
}

const DamagedLabelLookupTable: React.FC<DamagedLabelLookupTableProps> = ({
    parcelData,
    showParcelDetail,
    onSelectChange,
    selectedRowKeys = [],
    loading = false,
}) => {
    const { t } = useTranslation();
    const { data: channelData } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const { getStatusText, getStatusColor } = useStatusInternationalization();

    const columns: TableColumnsType<OrderType> = useMemo(
        () => [
            {
                title: t('delivery.parcels.label.tracking'),
                dataIndex: 'trackingNumber',
                key: 'trackingNumber',
                align: 'left',
                width: 180,
                ellipsis: true,
                render: (text: string, record) => (
                    <Button
                        type="link"
                        onClick={() => showParcelDetail(record)}
                        style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}
                    >
                        {text}
                    </Button>
                ),
            },
            {
                title: t('delivery.parcels.label.order'),
                dataIndex: 'channelOrderNumber',
                key: 'channelOrderNumber',
                align: 'left',
                width: 160,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('damagedLabelLookup.filters.senderName'),
                dataIndex: 'senderFullName',
                key: 'senderFullName',
                align: 'left',
                width: 120,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('damagedLabelLookup.filters.senderAddress'),
                dataIndex: 'senderAddressText',
                key: 'senderAddressText',
                align: 'left',
                width: 200,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('damagedLabelLookup.filters.senderPostalCode'),
                dataIndex: 'senderPostalCode',
                key: 'senderPostalCode',
                align: 'left',
                width: 100,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('damagedLabelLookup.filters.recipientName'),
                dataIndex: 'recipientFullName',
                key: 'recipientFullName',
                align: 'left',
                width: 120,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('damagedLabelLookup.filters.recipientAddress'),
                dataIndex: 'recipientAddressText',
                key: 'recipientAddressText',
                align: 'left',
                width: 200,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('damagedLabelLookup.filters.recipientPostalCode'),
                dataIndex: 'receiverZipcode',
                key: 'receiverZipcode',
                align: 'left',
                width: 100,
                ellipsis: true,
                render: (text: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                ),
            },
            {
                title: t('delivery.parcels.label.client'),
                dataIndex: 'channelId',
                key: 'channelId',
                align: 'left',
                width: 160,
                ellipsis: true,
                render: (_text, record) => {
                    const channel = channelData?.getAllChannels?.find(
                        (item) => String(item.id) === String(record.channelId)
                    );
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {channel?.channelName || '-'}
                        </span>
                    );
                },
            },
            {
                title: t('delivery.parcels.label.status'),
                dataIndex: 'overallStatus',
                key: 'overallStatus',
                align: 'left',
                width: 140,
                ellipsis: true,
                render: (status: string) => {
                    const statusText = getStatusText(status);
                    const color = getStatusColor(status);
                    return (
                        <span
                            className={`inline-block px-2 py-1 rounded ${color}`}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {statusText}
                        </span>
                    );
                },
            },
            {
                title: t('delivery.parcels.label.prePickupHub'),
                dataIndex: 'prePickupHubName',
                key: 'prePickupHubName',
                align: 'left',
                width: 160,
                ellipsis: true,
                render: (value: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>
                ),
            },
            {
                title: t('delivery.parcels.label.pickupHub'),
                dataIndex: 'pickupHubName',
                key: 'pickupHubName',
                align: 'left',
                width: 160,
                ellipsis: true,
                render: (value: string | null | undefined) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>
                ),
            },
            {
                title: t('delivery.parcels.label.firstSortCode'),
                key: 'sortingCodeSegment1',
                align: 'left',
                width: 120,
                ellipsis: true,
                render: (_value, record) => {
                    const parts = record.sortingCode?.split('-') || [];
                    return <span style={{ whiteSpace: 'nowrap' }}>{parts[0] || '-'}</span>;
                },
            },
            {
                title: t('delivery.parcels.label.secondSortCode'),
                key: 'sortingCodeSegment2',
                align: 'left',
                width: 120,
                ellipsis: true,
                render: (_value, record) => {
                    const parts = record.sortingCode?.split('-') || [];
                    return <span style={{ whiteSpace: 'nowrap' }}>{parts[1] || '-'}</span>;
                },
            },
            {
                title: t('delivery.parcels.label.thirdSortCode'),
                key: 'sortingCodeSegment3',
                align: 'left',
                width: 120,
                ellipsis: true,
                render: (_value, record) => {
                    const parts = record.sortingCode?.split('-') || [];
                    return <span style={{ whiteSpace: 'nowrap' }}>{parts[2] || '-'}</span>;
                },
            },
        ],
        [channelData?.getAllChannels, getStatusColor, getStatusText, showParcelDetail, t]
    );

    const rowSelection = onSelectChange
        ? {
              selectedRowKeys,
              onChange: onSelectChange,
          }
        : undefined;

    return (
        <Table<OrderType>
            rowKey={(record) => record.trackingNumber || record.id}
            columns={columns}
            dataSource={parcelData}
            loading={loading}
            rowSelection={rowSelection}
            pagination={false}
            scroll={{ x: 'max-content' }}
            tableLayout="fixed"
            className="mb-4"
            size="middle"
        />
    );
};

export default DamagedLabelLookupTable;
